// Layer: presentation. The console shell.
//
// Previously `main.tsx` mounted the shift panel alone and the other four components
// were written but unreachable. This routes all of them.
import { useEffect, useMemo, useState } from "react";
import type { ApiActivity, ApiPhase, StoriaApi } from "../application/ports";
import { DecideAction, LoadShift } from "../application/shift-load";
import type { Playbook } from "../domain/types";
import { AuditView } from "./AuditView";
import { GuestJourneyView } from "./GuestJourneyView";
import { KpiView } from "./KpiView";
import { MergeReview } from "./MergeReview";
import { PlaybookPanel } from "./PlaybookPanel";
import { ShiftViewPanel } from "./ShiftView";

export type Tab = "guest" | "shift" | "playbooks" | "identity" | "audit" | "kpis";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "guest", label: "Guest" },
  { id: "shift", label: "Shift" },
  { id: "playbooks", label: "Playbooks" },
  { id: "identity", label: "Identity" },
  { id: "audit", label: "Audit" },
  { id: "kpis", label: "KPIs" },
];

/** Where a visitor who has no credential came from, and can go back to. */
export const LABS_URL = "https://solara.associates/labs/storia/";

/** One plain sentence per screen, because an operator opening this for the first time
 *  should not have to infer what a "shift" or a "merge candidate" is from the data in
 *  front of them. Shown above each panel, not in a tooltip nobody hovers. */
const BLURBS: Record<Tab, string> = {
  guest: "One guest's stay, told twice: what they experienced, and what caused it.",
  shift: "Everything STORIA wants to do right now. None of it has happened yet, a person decides.",
  playbooks: "The rules that turn a signal into a proposed action. Change one and the next signal follows it.",
  identity: "Two records that might be the same person. STORIA never merges them for you.",
  audit: "Every automated decision, hash-chained. If a row were altered or removed, the chain would say so.",
  kpis: "What changed for the business, measured over the window, not what STORIA did.",
};

export interface AppProps {
  api: StoriaApi;
  propertyId: string;
  propertyIds: string[];
  /** Shown in the Playbooks tab when the property has none of its own. */
  samplePlaybooks: readonly Playbook[];
  initialTab?: Tab;
  /** The operator whose console this is, shown beside the wordmark. Absent renders
   *  nothing: a console with no configured operator must not display somebody else's
   *  name, and the slot previously carried a hardcoded mark belonging to an unrelated
   *  venture. */
  operatorName?: string;
  signedIn: boolean;
  /** Starts the OIDC redirect. Absent when the host page supplies the credential. */
  onSignIn?: () => void;
  /** Reports when the API is being woken from cold. Absent renders no banner. */
  activity?: ApiActivity;
}

export function App({
  api,
  propertyId,
  propertyIds,
  samplePlaybooks,
  // Lands on the guest journey rather than the work queue. Shift is the screen an
  // operator lives in, but it is meaningless to anyone seeing STORIA for the first
  // time; the journey explains what the queue is for. Flip this to "shift" once there
  // is a pilot operator whose morning starts here.
  initialTab = "guest",
  operatorName,
  signedIn,
  onSignIn,
  activity,
}: AppProps) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const loadShift = useMemo(() => new LoadShift(api), [api]);
  const decideAction = useMemo(() => new DecideAction(api), [api]);
  const phase = useApiPhase(activity);

  if (!signedIn) {
    return (
      <div className="shell">
        <Topbar operatorName={operatorName} />
        <div className="content">
          <main className="stack" aria-label="Sign in required">
            <div className="alert" role="alert">
              <p>You are not signed in.</p>
              {onSignIn ? (
                <>
                  <p className="muted">
                    Sign in with your operator account to see this shift.
                  </p>
                  <button type="button" className="primary" onClick={onSignIn}>
                    Sign in
                  </button>
                </>
              ) : (
                <p className="muted">
                  Open the console from your operator portal so it can hand STORIA a
                  credential.
                </p>
              )}
            </div>
            {/* A reload drops the credential, because it is held in memory and never in
                storage. That is deliberate, and it means this panel is what a visitor
                who refreshes the demo sees. Leaving them on a bare error with no header
                and no way out made a working design decision look like a broken page. */}
            <p className="muted">
              Arrived here from the STORIA write-up?{" "}
              <a href={LABS_URL}>Go back to the overview</a>, which has a fresh link in.
            </p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <Topbar operatorName={operatorName}>
        <nav aria-label="Sections">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={item.id === tab ? "tab tab-active" : "tab"}
              aria-current={item.id === tab ? "page" : undefined}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </Topbar>

      <div className="content">
        {/* One banner for the whole shell rather than one per panel, so switching tabs
            mid-wake does not lose the explanation. `status`, not `alert`: nothing has
            gone wrong, and an assertive live region would interrupt a screen reader to
            say so. */}
        {phase === "waking" ? (
          <div className="waking" role="status">
            Waking the demo up, this takes a few seconds.
          </div>
        ) : null}
        <p className="blurb">{BLURBS[tab]}</p>
        {tab === "guest" ? <GuestJourneyView api={api} /> : null}
        {tab === "shift" ? (
          <ShiftViewPanel
            propertyId={propertyId}
            loadShift={loadShift}
            decideAction={decideAction}
          />
        ) : null}
        {tab === "playbooks" ? (
          <PlaybookPanel api={api} propertyId={propertyId} fallback={samplePlaybooks} />
        ) : null}
        {tab === "identity" ? <MergeReview api={api} /> : null}
        {tab === "audit" ? <AuditView api={api} /> : null}
        {tab === "kpis" ? <KpiView api={api} propertyIds={propertyIds} /> : null}
      </div>
    </div>
  );
}

/** The brand surface. Extracted so the signed-out state carries it too: a visitor with
 *  no credential should still be able to see what they have opened. */
function Topbar({
  operatorName,
  children,
}: {
  operatorName?: string;
  children?: React.ReactNode;
}) {
  return (
    <header className="topbar">
      <span className="wordmark">STORIA</span>
      {operatorName ? (
        <span className="parent-mark" aria-label={`Operator: ${operatorName}`}>
          {operatorName}
        </span>
      ) : null}
      {children}
    </header>
  );
}

function useApiPhase(activity: ApiActivity | undefined): ApiPhase {
  const [phase, setPhase] = useState<ApiPhase>("idle");
  useEffect(() => {
    if (!activity) return;
    return activity.subscribe(setPhase);
  }, [activity]);
  return phase;
}
